import { formatCurrency, formatDate, NgTemplateOutlet, PercentPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { AdSpendFeeType, Currency, Plan } from "@front/m19-api-client";
import { CurrencySymbols } from "@front/m19-models";
import { AuthService, BillingPlanService, BillingService } from "@front/m19-services";
import { BillingInfoFormComponent, IBadgeComponent, IIconComponent, ITooltipDirective } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { OnboardingService } from "@m19-board/services/onboarding.service";
import { DateTime } from "luxon";
import { switchMap } from "rxjs";
import { environment } from "../../environments/environment";

const PRO_FREE_TEST_DAYS = 30;
const SUPPORTED_CURRENCIES = [Currency.EUR, Currency.USD];

// TODO(clement): still WIP. The logic here is not finalized - purpose is to showcase the upgrade flow from Autopilot to Pro
// and also Merge with UpgradeToProModalComponent once design is valiated and the upgrade from Autopilot is implemented
@Component({
  selector: "app-upgrade-to-pro-from-autopilot-modal",
  imports: [
    TranslocoDirective,
    NgTemplateOutlet,
    PercentPipe,
    BillingInfoFormComponent,
    IIconComponent,
    IBadgeComponent,
    ITooltipDirective,
  ],
  templateUrl: "./upgrade-to-pro-from-autopilot-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgradeToProFromAutopilotModalComponent {
  private readonly billingService = inject(BillingService);
  private readonly billingPlanService = inject(BillingPlanService);
  private readonly authService = inject(AuthService);
  private readonly onboardingService = inject(OnboardingService);

  protected readonly stripePublicKey = environment.stripePublicKey;
  readonly AdSpendFeeType = AdSpendFeeType;
  readonly PRO_FREE_TEST_DAYS = PRO_FREE_TEST_DAYS;

  private readonly organizationGroup = toSignal(this.billingService.organizationOwner$);
  private readonly organization = computed(() => this.organizationGroup()?.organization);
  readonly customer = computed(() => this.organizationGroup()?.customer);
  readonly customerCountry = linkedSignal<string | undefined>(() => this.customer()?.country);
  readonly requiredCard = computed(() => !this.organization()?.noCbRequired);

  private readonly user = toSignal(this.authService.loggedUser$);
  readonly locale = computed(() => this.user()?.locale ?? "en-US");

  private readonly autopilotPlans = toSignal(this.billingPlanService.listBillingPlans(Plan.AUTOPILOT));
  private readonly currentAutopilotPlan = computed(() => {
    const organization = this.organization();
    if (organization?.billingPlan?.plan !== Plan.AUTOPILOT) {
      return undefined;
    }
    const billingPlanId = organization.billingPlanId ?? organization.billingPlan.billingPlanId;
    return this.autopilotPlans()?.find((p) => p.billingPlanId === billingPlanId);
  });

  private readonly currency = computed(() => {
    const currency = this.currentAutopilotPlan()?.currency ?? this.organization()?.billingPlan?.currency;
    return currency && SUPPORTED_CURRENCIES.includes(currency) ? currency : Currency.EUR;
  });

  private readonly proBillingPlans = toSignal(
    toObservable(this.customerCountry).pipe(switchMap((c) => this.billingPlanService.listProBillingPlans(c))),
  );
  readonly selectedPlan = computed(() => this.proBillingPlans()?.find((p) => p.currency === this.currency()));
  private readonly planAmount = computed(() => this.selectedPlan()?.flatFee?.amount);
  readonly isAmountLoading = computed(() => this.planAmount() === undefined);
  readonly formattedPlanAmount = computed(() => this.formatAmount(this.planAmount() ?? 0));

  readonly formattedAutopilotAmount = computed(() => {
    const autopilotPlan =
      this.currentAutopilotPlan() ?? this.autopilotPlans()?.find((p) => p.currency === this.currency());
    return autopilotPlan?.flatFee?.amount ? this.formatAmount(autopilotPlan.flatFee.amount) : "";
  });

  readonly autopilotCredit = computed(() => {
    const organization = this.organization();
    return unusedAutopilotAmount(
      this.currentAutopilotPlan()?.flatFee?.amount,
      organization?.subscriptionBillingCycleAnchorDate ?? organization?.subscriptionStartDate,
      DateTime.utc(),
    );
  });
  readonly formattedAutopilotCredit = computed(() => this.formatAmount(this.autopilotCredit(), "1.2-2"));

  readonly autopilotFreeTestEndDate = computed(() => {
    const organization = this.organization();
    if (organization?.billingPlan?.plan !== Plan.FREE_AUTOPILOT_TEST || !organization.freeTestAutopilotEndDate) {
      return undefined;
    }
    const endDate = DateTime.fromISO(organization.freeTestAutopilotEndDate, { zone: "utc" });
    return endDate >= DateTime.utc().startOf("day") ? this.formatLongDate(endDate) : undefined;
  });
  readonly autopilotUpgradeCanceled = computed(
    () => !!this.autopilotFreeTestEndDate() && !!this.organization()?.freeTestAutopilotUpgradeDate,
  );

  private readonly proFreeTestEnd = computed(() => {
    const organizationGroup = this.organizationGroup();
    return organizationGroup && this.onboardingService.entitledForFreeTestSubscription(organizationGroup)
      ? DateTime.utc().plus({ days: PRO_FREE_TEST_DAYS })
      : undefined;
  });
  readonly proFreeTestEndDate = computed(() => {
    const end = this.proFreeTestEnd();
    return end ? this.formatLongDate(end) : undefined;
  });
  // Billed in arrears: fixed and ad spend fees are invoiced together, one month after the plan starts
  private readonly firstProChargeDate = computed(() => (this.proFreeTestEnd() ?? DateTime.utc()).plus({ months: 1 }));
  readonly formattedFirstProChargeDate = computed(() => this.formatLongDate(this.firstProChargeDate()));
  readonly formattedFirstProChargeAmount = computed(() =>
    this.formatAmount(Math.max(0, (this.planAmount() ?? 0) - this.autopilotCredit()), "1.2-2"),
  );
  readonly formattedRecurringProChargeDate = computed(() =>
    this.formatLongDate(this.firstProChargeDate().plus({ months: 1 })),
  );
  readonly formattedZeroAmount = computed(() => this.formatAmount(0, "1.2-2"));

  readonly proFeatures = [
    "explore-pro-modal.pro_feature_accounts",
    "explore-pro-modal.pro_feature_strategies",
    "explore-pro-modal.pro_feature_reporting",
  ];
  readonly moreProFeatures = ["explore-pro-modal.pro_feature_onboarding", "explore-pro-modal.pro_feature_support"];
  readonly showMoreFeatures = signal(false);

  upgrade() {
    // TODO(clement): call the upgrade from Autopilot endpoint once it exists
  }

  private formatAmount(amount: number, digitsInfo = "1.0-0") {
    return formatCurrency(amount, this.locale(), CurrencySymbols[this.currency()], this.currency(), digitsInfo);
  }

  private formatLongDate(date: DateTime): string {
    return formatDate(date.toISODate()!, "longDate", this.locale());
  }
}

// TODO(clement): The prorata of the current Autopilot billing month - use the preview invoice endpoint to get the exact unused amount
export function unusedAutopilotAmount(
  amount: number | undefined,
  billingCycleAnchorDate: string | undefined,
  now: DateTime,
): number {
  if (!amount || !billingCycleAnchorDate) {
    return 0;
  }
  const anchor = DateTime.fromISO(billingCycleAnchorDate, { zone: "utc" });
  if (anchor > now) {
    return 0;
  }
  const periodStart = anchor.plus({ months: Math.floor(now.diff(anchor, "months").months) });
  const periodEnd = periodStart.plus({ months: 1 });
  const unusedRatio = periodEnd.diff(now).toMillis() / periodEnd.diff(periodStart).toMillis();
  return Math.round(amount * unusedRatio * 100) / 100;
}
